import React, { useState } from "react";
import DOMPurify from "dompurify";
import { EventForm } from "./EventForm";
import { useEventStore } from "../stores/eventStore";
import { useCalendarStore } from "../stores/calendarStore";
import { useGoogleSyncStore } from "../stores/googleSyncStore";
import type { ApiCalendarEntry, ApiDate, CreateEventInput, UpdateEventInput } from "../lib/types";
import { formatDateRange } from "../lib/dateUtils";
import { refreshReminders } from "../lib/reminderScheduler";
import { formatFrequencyLabel, parsePickerStateFromRrule } from "../lib/rrule";

const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ["a", "br", "b", "i", "em", "strong", "p", "span", "ul", "ol", "li"],
  ALLOWED_ATTR: ["href", "target", "rel"],
};

function sanitizeHtml(html: string): string {
  // Force all links to open in new tab with noopener
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  const clean = DOMPurify.sanitize(html, SANITIZE_CONFIG);
  DOMPurify.removeHook("afterSanitizeAttributes");
  return clean;
}

function hasHtmlTags(text: string): boolean {
  return /<[a-z][\s\S]*?>/i.test(text);
}

function formatReminderLabel(minutes: number, hasTime: boolean): string {
  if (minutes === 0) return hasTime ? "At time of event" : "On the day (midnight)";
  if (minutes < 60) return `${minutes} minutes before`;
  if (minutes < 24 * 60) {
    const h = minutes / 60;
    return `${h} hour${h === 1 ? "" : "s"} before`;
  }
  const d = minutes / (24 * 60);
  if (d >= 7 && d % 7 === 0) {
    const w = d / 7;
    return `${w} week${w === 1 ? "" : "s"} before`;
  }
  return `${d} day${d === 1 ? "" : "s"} before`;
}

interface EventModalProps {
  event?: ApiCalendarEntry | null;
  defaultDate?: ApiDate;
  mode: "view" | "create" | "edit";
  onClose: () => void;
}

export function EventModal({ event, defaultDate, mode, onClose }: EventModalProps) {
  const [currentMode, setCurrentMode] = useState(mode);
  const [loading, setLoading] = useState(false);
  const calendars = useCalendarStore((s) => s.calendars);
  const createEvent = useEventStore((s) => s.create);
  const updateEvent = useEventStore((s) => s.update);
  const removeEvent = useEventStore((s) => s.remove);
  const isSyncedCalendar = useGoogleSyncStore((s) => s.isSyncedCalendar);
  const getSyncSourceType = useGoogleSyncStore((s) => s.getSyncSourceType);

  const isEventSynced = event ? isSyncedCalendar(event.calendarId) : false;
  const syncSourceType = event ? getSyncSourceType(event.calendarId) : null;

  const handleSubmit = async (calendarId: string, data: CreateEventInput | UpdateEventInput) => {
    // Editing an existing event that's already part of a series applies
    // the change to every occurrence — confirm before doing that. We
    // intentionally don't prompt when adding recurrence to a previously
    // one-shot event since that's a deliberate user choice in the form.
    if (currentMode === "edit" && event?.rrule) {
      const ok = confirm(
        "This is a recurring event. Saving will apply your changes to the entire series. Continue?",
      );
      if (!ok) return;
    }
    setLoading(true);
    try {
      if (currentMode === "create") {
        await createEvent(calendarId, data as CreateEventInput);
      } else if (event) {
        await updateEvent(event.calendarId, event.id, data);
      }
      refreshReminders();
      onClose();
    } catch {
      // Error handled by store
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    if (event.rrule) {
      const ok = confirm(
        "This is a recurring event. Deleting it removes the entire series, including past and future occurrences. Continue?",
      );
      if (!ok) return;
    }
    setLoading(true);
    try {
      await removeEvent(event.calendarId, event.id);
      refreshReminders();
      onClose();
    } catch {
      // Error handled by store
    } finally {
      setLoading(false);
    }
  };

  const calendar = event ? calendars.find((c) => c.id === event.calendarId) : null;

  const modalTitle =
    currentMode === "view"
      ? (event?.heading ?? "Event")
      : currentMode === "create"
        ? "Create event"
        : "Update event";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{modalTitle}</h2>
            {currentMode === "view" && event && <p className="muted">{formatDateRange(event)}</p>}
          </div>
          <button className="modal-close" aria-label="Close modal" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {currentMode === "view" && event ? (
            <div className="event-detail">
              <div className="event-detail-meta">
                {calendar && (
                  <div className="event-detail-meta-row">
                    <span
                      className="calendar-color-dot"
                      style={{ background: event.color ?? calendar.color }}
                    />
                    {calendar.name}
                  </div>
                )}
              </div>

              {event.content && (
                <div className="event-detail-row">
                  <span className="event-detail-label">Notes</span>
                  {hasHtmlTags(event.content) ? (
                    <div
                      className="event-detail-desc"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.content) }}
                    />
                  ) : (
                    <p className="event-detail-desc">{event.content}</p>
                  )}
                </div>
              )}

              {event.reminderMinutesBefore != null && (
                <div className="event-detail-row">
                  <span className="event-detail-label">Reminder</span>
                  <p className="event-detail-desc">
                    {formatReminderLabel(event.reminderMinutesBefore, event.time != null)}
                  </p>
                </div>
              )}

              {event.rrule && (
                <div className="event-detail-row">
                  <span className="event-detail-label">Repeats</span>
                  <p className="event-detail-desc">
                    {formatFrequencyLabel(parsePickerStateFromRrule(event.rrule))}
                  </p>
                </div>
              )}

              <hr className="event-detail-divider" />

              {isEventSynced ? (
                <p className="muted" style={{ fontSize: "0.8rem" }}>
                  Synced from {syncSourceType === "ics" ? "ICS feed" : syncSourceType === "caldav" ? "CalDAV" : "Google Calendar"}
                </p>
              ) : (
                <div className="event-detail-actions">
                  <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={loading}>
                    Delete
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => setCurrentMode("edit")}>
                    Edit
                  </button>
                </div>
              )}
            </div>
          ) : (
            <EventForm
              calendars={calendars}
              event={currentMode === "edit" ? event : null}
              defaultDate={defaultDate}
              defaultCalendarId={calendars[0]?.id}
              onSubmit={handleSubmit}
              onCancel={onClose}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
}
